/** Raridades: ordem, multiplicadores, contagem de atributos e sorteio por fonte. */
import { GameConfig } from "../config/GameConfig";
import type { Rarity } from "./EquipmentTypes";

export const RARITIES = GameConfig.rarity.order as readonly Rarity[];

export type RaritySource = keyof typeof GameConfig.rarity.weights;

/** Cor por raridade (three.js hex). */
export const RARITY_COLOR: Record<Rarity, number> = {
  common: 0x9aa0b4,
  uncommon: 0x4caf68,
  rare: 0x3d8bff,
  epic: 0xb15cff,
  legendary: 0xffb020,
  mythic: 0xff4d6d,
};
/** Cor por raridade como string CSS (#rrggbb). */
export function rarityCss(r: Rarity): string {
  return "#" + RARITY_COLOR[r].toString(16).padStart(6, "0");
}

export function rarityMult(r: Rarity): number {
  return GameConfig.rarity.mult[r];
}
export function secondaryCount(r: Rarity): number {
  return GameConfig.rarity.secondary[r];
}
export function rarityIndex(r: Rarity): number {
  return RARITIES.indexOf(r);
}
/** Nº de "marcas" para leitura de raridade sem depender só de cor (acessibilidade). */
export function rarityMarks(r: Rarity): number {
  return rarityIndex(r) + 1; // common=1 ... mythic=6
}

/** 10.6V — Nível MÍNIMO do Caçador para EQUIPAR itens da raridade dada. */
export function minLevelForRarity(r: Rarity): number {
  switch (r) {
    case "common": return 0;
    case "uncommon": return 3;
    case "rare": return 10;
    case "epic": return 20;
    case "legendary": return 35;
    case "mythic": return 50;
  }
}

/** 10.9AN — Nível MÍNIMO para EQUIPAR armadura do TIER da região (1 Forest, 2 Wilds, 3 Tempest).
 * Alinhado à raridade (épico 20 / lendário 35) pra escada ficar coerente. */
export function minLevelForTier(tier: number): number {
  // 10.9AO — T4/T5/T6 liberados (Nv50/70/90); a Ameaça Crescente acompanha (vida efetiva)
  // 10.9FZ — tiers de fim de jogo: T7=150, T8=200, T9=250, T10=300, T11=400, T12=500, T13=700, T14=1000
  const gates: Record<number, number> = { 14: 1000, 13: 700, 12: 500, 11: 400, 10: 300, 9: 250, 8: 200, 7: 150, 6: 90, 5: 70, 4: 50, 3: 35, 2: 20 };
  return gates[Math.min(14, Math.max(0, tier))] ?? 0;
}
/** 10.9AN — Tier MÁXIMO de armadura que pode DROPAR pro nível (peitoral T3 não cai pro nv12). */
export function maxArmorTierForLevel(playerLevel: number): number {
  // 10.9FZ — o MAIOR tier que o nível permite (usado pra dropar só o que o Caçador pode usar)
  const th: [number, number][] = [[1000, 14], [700, 13], [500, 12], [400, 11], [300, 10], [250, 9], [200, 8], [150, 7], [90, 6], [70, 5], [50, 4], [35, 3], [20, 2]];
  for (const [lv, t] of th) if (playerLevel >= lv) return t;
  return 1;
}

/** 10.6V — Raridade MÁXIMA que pode DROPAR para um Caçador daquele nível.
 * Impede legendárias caindo pra lv 5 (frustrante — não pode equipar). */
export function maxRarityForLevel(playerLevel: number): Rarity {
  if (playerLevel >= 45) return "mythic";
  if (playerLevel >= 30) return "legendary";
  if (playerLevel >= 15) return "epic";
  if (playerLevel >= 6) return "rare";
  if (playerLevel >= 2) return "uncommon";
  return "common";
}

/** Reduz `r` para não passar do teto de nível. */
export function capRarityByLevel(r: Rarity, playerLevel: number): Rarity {
  const max = maxRarityForLevel(playerLevel);
  return rarityIndex(r) > rarityIndex(max) ? max : r;
}

/** Sorteia uma raridade pelos pesos da fonte. */
export function rollRarity(source: RaritySource): Rarity {
  const w = GameConfig.rarity.weights[source] as Record<Rarity, number>;
  let total = 0;
  for (const r of RARITIES) total += w[r] ?? 0;
  if (total <= 0) return "common";
  let x = Math.random() * total;
  for (const r of RARITIES) {
    x -= w[r] ?? 0;
    if (x <= 0) return r;
  }
  return "common";
}
